import React, { useState } from "react";
import axios from "axios";
import { Button, Carousel, Col, Image, Row } from "react-bootstrap";
import "./WaterUndergroundCarousel.css";
import WaterImageUpload from "./components/WaterImageUpload";
import { useMutation, useQuery } from "@tanstack/react-query";
import { WaterFileDTO } from "../../dtos/WaterFileDTO";
import { NetworkCalls } from "../../NetworkCalls";
import DeleteDialog from "../../components/DeleteDialog";

interface WaterUndergroundCarouselProps {
    waterId?: string;
    visible?: boolean;
    register?: any;
    setValue?: any;
}

export default function WaterUndergroundCarousel({
                                                     waterId,
                                                     visible,
                                                     register,
                                                     setValue,
                                                 }: WaterUndergroundCarouselProps) {
    const [activeIndex, setActiveIndex] = useState<number>(0);
    const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);

    const { isPending, isError, data, error, refetch } = useQuery<WaterFileDTO[]>({
        queryKey: [`waterUndergroundMapList-${waterId}`],
        // staleTime: 2 * 60 * 1000, // is in milliseconds
        enabled: visible && !!waterId,
        queryFn: async () => {
            let response = await axios.get(`/water/${waterId}/waterUndergroundMap`);

            const mapped = response.data
                .filter((file: any) => file?.fileType && file?.url)
                .map((file: any) => ({
                    id: file.id,
                    filename: file.fileName ?? "Unbenannte Datei",
                    mimeType: file.fileType,
                    url: file.url,
                }));
            return mapped as WaterFileDTO[];
        }
    });

    const deleteImage = useMutation({
        mutationFn: async () => {
            if (!data || !waterId)
                return;

            const fileId = data[activeIndex].id;
            await NetworkCalls.deleteWaterUndergroundImage(fileId, waterId);
        },
        onSuccess: async () => {
            setActiveIndex(0);
            await refetch();
        },
    });

    if (!visible || !waterId) {
        return null;
    }

    if (isPending) {
        return <p>Lade Tiefenkarten...</p>
    }

    if (isError && error) {
        return <div className="text-danger">Fehler: {error.message}</div>;
    }

    if (!data || data.length === 0) {
        return <>
            <p>Keine Tiefenkarten vorhanden.</p>
            <WaterImageUpload waterId={waterId} afterSuccess={refetch} />
        </>;
    }

    const renderDeleteDialog = () => {
        return <>
            <DeleteDialog
                onDeleteConfirm={() => {
                    deleteImage.mutate();
                    setShowDeleteDialog(false);
                }}
                title="Bild löschen"
                message="Soll das ausgewählte Bild wirklich gelöscht werden?"
                show={showDeleteDialog}
                onHandleClose={() => setShowDeleteDialog(false)}
            />
        </>
    }

    const renderFooter = () => {
        return <>
            <Row key={"protectionViewFooter"}>
                <Col xs={12}>
                    <WaterImageUpload waterId={waterId} afterSuccess={refetch} />
                    <Button className={"ms-2"} variant={"danger"} onClick={_ => setShowDeleteDialog(true)} >
                        Bild löschen
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="icon icon-tabler icon-tabler-trash ms-2"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            strokeWidth="2"
                            stroke="currentColor"
                            fill="none"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                            <path d="M4 7l16 0"></path>
                            <path d="M10 11l0 6"></path>
                            <path d="M14 11l0 6"></path>
                            <path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12"></path>
                            <path d="M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3"></path>
                        </svg>
                    </Button>
                </Col>
            </Row>
        </>
    }

    return <>
        <div className="d-flex">
            {/* Thumbnail-Leiste */}
            <div className="d-flex flex-column align-items-center me-3" style={{ width: "100px" }}>
                {data.map((file, idx) => (
                    <Image
                        key={file.id}
                        src={file.url}
                        thumbnail
                        onClick={() => setActiveIndex(idx)}
                        className={`mb-2 ${activeIndex === idx ? "border-primary border-2" : ""}`}
                        style={{ cursor: "pointer", width: "100%", height: "auto", objectFit: "cover" }}
                    />
                ))}
            </div>

            {/* Hauptbild mit sichtbarer Navigation */}
            <div className="flex-fill">
                <Carousel
                    activeIndex={activeIndex}
                    onSelect={(selectedIndex) => setActiveIndex(selectedIndex)}
                    controls
                    indicators={false}
                    interval={null}
                >
                    {data.map((file) => (
                        <Carousel.Item key={file.id}>
                            <div className="d-flex justify-content-center" style={{ height: "500px" }}>
                                {file.mimeType.startsWith("image/") ? (
                                    <img
                                        src={file.url}
                                        alt={file.filename}
                                        className="d-block"
                                        style={{ maxHeight: "100%", maxWidth: "100%", objectFit: "contain" }}
                                    />
                                ) : file.mimeType === "application/pdf" ? (
                                    <iframe
                                        src={file.url}
                                        title={file.filename}
                                        width="100%"
                                        height="100%"
                                        style={{ border: "none" }}
                                    />
                                ) : (
                                    <p>
                                        <a href={file.url} target="_blank" rel="noopener noreferrer">
                                            {file.filename} herunterladen
                                        </a>
                                    </p>
                                )}
                            </div>
                            <Carousel.Caption>
                                <h5>{file.filename}</h5>
                            </Carousel.Caption>
                        </Carousel.Item>
                    ))}
                </Carousel>
            </div>
        </div>
        <div className={'flex-fill'}>
            {renderFooter()}
        </div>

        {renderDeleteDialog()}
    </>
    ;
}
